export const toVoucherNumber = (value) => {
    if (value === null || value === undefined) return null
    if (typeof value === 'string' && value.trim() === '') return null

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

export const formatIDR = (value = 0) => `Rp ${Intl.NumberFormat('en-ID').format(Math.max(0, Math.floor(value || 0)))}`

export const normalizeVoucherCompanySlug = (value) => `${value || ''}`.trim().toLowerCase()

export const normalizeVoucherSku = (value) => `${value || ''}`.trim().toLowerCase()

export const getVoucherCompanySlug = (memberVoucher) => {
    const voucher = memberVoucher?.voucher || memberVoucher || {}

    return [
        voucher?.partner?.company_slug,
        voucher?.company_slug,
        memberVoucher?.company_slug,
        memberVoucher?.partner?.company_slug
    ]
        .map(normalizeVoucherCompanySlug)
        .find(Boolean) || ''
}

export const voucherMatchesCompany = (memberVoucher, companySlug) => {
    const normalizedCompanySlug = normalizeVoucherCompanySlug(companySlug)
    const voucherCompanySlug = getVoucherCompanySlug(memberVoucher)

    return Boolean(normalizedCompanySlug && voucherCompanySlug && voucherCompanySlug === normalizedCompanySlug)
}

export const getVoucherDiscountMeta = (memberVoucher) => {
    const voucher = memberVoucher?.voucher || memberVoucher || {}
    const voucherType = memberVoucher?.voucher_type || voucher?.type
    const discountCalculationType = memberVoucher?.discount_calculation_type || voucher?.discount_calculation_type
    const discountValue =
        toVoucherNumber(memberVoucher?.discount_value) ??
        toVoucherNumber(voucher?.discount_value)
    const legacyAmount = toVoucherNumber(memberVoucher?.amount)

    if (voucherType === 'free_delivery') {
        return {
            type: 'free_delivery',
            value: 0,
            label: 'Free delivery',
            sku: voucher?.sku || memberVoucher?.sku || ''
        }
    }

    if (voucherType === 'amount' && discountCalculationType === 'percentage' && discountValue > 0) {
        return {
            type: 'percentage',
            value: discountValue,
            label: `${Number(discountValue.toFixed(2)).toLocaleString('id-ID')}%`,
            sku: voucher?.sku || memberVoucher?.sku || ''
        }
    }

    if (voucherType === 'amount' && discountCalculationType === 'fixed_amount' && discountValue > 0) {
        return {
            type: 'fixed_amount',
            value: discountValue,
            label: formatIDR(discountValue),
            sku: voucher?.sku || memberVoucher?.sku || ''
        }
    }

    if (legacyAmount > 0) {
        return {
            type: 'fixed_amount',
            value: legacyAmount,
            label: formatIDR(legacyAmount),
            sku: voucher?.sku || memberVoucher?.sku || '',
            legacy: true
        }
    }

    return {
        type: voucherType || 'free',
        value: 0,
        label: voucherType === 'free' ? 'Free item' : '-',
        sku: voucher?.sku || memberVoucher?.sku || ''
    }
}

export const voucherMatchesItems = (memberVoucher, items = []) => {
    const meta = getVoucherDiscountMeta(memberVoucher)
    const requiredSku = normalizeVoucherSku(meta.sku)

    if (!requiredSku) {
        return true
    }

    return (items || []).some((item) => {
        const candidates = [
            item?.item_sku,
            item?.sku,
            item?.product_sku,
            item?.menu_sku,
            item?.voucher_sku
        ]

        return candidates.some((candidate) => normalizeVoucherSku(candidate) === requiredSku)
    })
}

export const getVoucherDiscountAmount = (memberVoucher, subtotal = 0, options = {}) => {
    const meta = getVoucherDiscountMeta(memberVoucher)
    const safeSubtotal = Math.max(0, toVoucherNumber(subtotal) || 0)
    const shippingAmount = Math.max(0, toVoucherNumber(options.shippingAmount) || 0)

    if (meta.type === 'free_delivery') {
        return voucherMatchesItems(memberVoucher, options.items || []) ? shippingAmount : 0
    }

    if (meta.type === 'percentage') {
        return Math.min(safeSubtotal, Math.floor((safeSubtotal * meta.value) / 100))
    }

    if (meta.type === 'fixed_amount') {
        return Math.min(safeSubtotal, Math.max(0, meta.value || 0))
    }

    return 0
}

export const formatVoucherDiscount = (memberVoucher) => getVoucherDiscountMeta(memberVoucher).label
